/**
 * PATH: packages/ui/src/kanban/utils/cardReorder.ts
 * FILE_ID: KANBAN-CARD-REORDER-001
 * PURPOSE: Pure I→O helpers for drag-to-reorder cards inside (or across) a
 *          Kanban lane — turn a pointer drop (clientY over the lane's card
 *          elements) into a target index, then into a float `position` value
 *          the move endpoint persists.
 * WHY: FEAT-2026-07-02-KANBAN-DRAG-REORDER — cards could be dragged BETWEEN
 *      lanes but not re-arranged WITHIN one, and a cross-lane drop always
 *      appended to the bottom (position ignored). The client requirement is
 *      "manually updated positions get the highest priority" and must survive
 *      a refresh, so a drop has to compute a stable numeric `position` that
 *      slots the card between its new neighbours. Keeping this math in a
 *      dependency-free module makes it unit-testable in isolation (no DOM,
 *      no React) and reusable by same-lane and cross-lane drop paths alike.
 * FLOW:
 *   clientY + card midpoints ─▶ computeDropIndexFromMidpoints ─▶ drop index
 *   ordered lane cards + drop index ─▶ computeReorderPosition ─▶ float position
 * RELATED:
 *   - utils.ts (compareCards): sorts a lane by `position` (after `pinned`), so
 *     the value produced here determines where the card lands after refetch.
 *   - components/KanbanLane.tsx: measures the DOM + calls these on drop.
 *   - components/board-view/mutations/useCardMoveMutation.ts: persists it.
 */

/** Spacing between adjacent card positions. Matches the BE's "append at
 *  `max + 10`" convention (see Card.position docs) so a freshly reindexed
 *  lane and a FE-computed insert use the same scale. A midpoint insert
 *  between two step-10 neighbours has ~log2(10) ≈ 3 subdivisions before it
 *  needs the float precision that JS numbers give for free — plenty for
 *  interactive reordering without a server-side reindex. */
export const POSITION_STEP = 10

/** A finite numeric position, or `null`. Narrow, defensive read so callers can
 *  pass raw `Card`-shaped objects whose `position` may be `undefined` / `NaN`
 *  (legacy rows, optimistic literals) without special-casing at every site. */
function finitePosition(
  card: { position?: number | null } | null | undefined,
): number | null {
  const p = card?.position
  return typeof p === 'number' && Number.isFinite(p) ? p : null
}

/**
 * Given the vertical MIDPOINT (viewport px) of each card currently rendered in
 * a lane, in visual order, return the index at which a card dropped at
 * `clientY` should be inserted (0 = above the first card, `midpoints.length` =
 * below the last). A card is "before" index `i` when the cursor is above that
 * card's midpoint — the standard list-reorder hit test.
 *
 * Pure + framework-free: the caller reads `getBoundingClientRect()` and hands
 * the midpoints in; this does only arithmetic so it is trivially testable.
 */
export function computeDropIndexFromMidpoints(
  clientY: number,
  midpoints: readonly number[],
): number {
  for (let i = 0; i < midpoints.length; i++) {
    if (clientY < midpoints[i]) return i
  }
  return midpoints.length
}

/**
 * Compute the float `position` that lands the moving card at `dropIndex` within
 * `orderedCards` (the lane's cards in their current VISUAL order, which may or
 * may not already include the moving card — it is filtered out either way).
 *
 * Strategy: read the STORED positions of the drop's immediate neighbours and
 * return the midpoint between them. Edge/degenerate cases are handled so the
 * result is always a usable number:
 *   - drop at the top    → just below the first neighbour's position
 *   - drop at the bottom → just above the last neighbour's position
 *   - both neighbours present + monotonic → midpoint
 *   - neighbours equal / non-monotonic (stale/duplicate stored positions) →
 *     nudge a half-step above the upper neighbour so the card still lands after
 *     it rather than collapsing onto an identical key
 *   - no neighbours carry a stored position (empty lane, legacy rows) →
 *     `POSITION_STEP`
 *
 * @param orderedCards lane cards in visual order (with optional `position`)
 * @param dropIndex    insertion index from computeDropIndexFromMidpoints
 * @param movingCardId id of the card being moved (excluded from neighbour math)
 */
/**
 * A minimal vertical rect — the top/bottom/height a card tile reports via
 * `getBoundingClientRect()`. Kept structural (not the DOM type) so this stays
 * a pure, DOM-free helper that unit tests can drive with plain literals.
 */
export interface TileVerticalRect {
  top: number
  bottom: number
  height: number
}

/**
 * FEAT-2026-07-03-KANBAN-DROP-LINE — given the drag cursor's `clientY` and the
 * vertical rects of the lane's rendered card tiles (in visual order), return
 * the viewport Y at which a live insertion line should be drawn, or `null` when
 * there are no tiles (empty lane → no line; the empty-state hint covers it).
 *
 * It reuses `computeDropIndexFromMidpoints` so the previewed line is guaranteed
 * to sit at the SAME boundary the drop will resolve to — the preview can never
 * disagree with where the card actually lands. The boundary is:
 *   - above the first tile   → that tile's top
 *   - below the last tile    → that tile's bottom
 *   - between two tiles       → the midpoint of the gap between them
 * Pure arithmetic (no DOM), so it is trivially testable in isolation.
 */
export function computeDropBoundaryY(
  clientY: number,
  rects: readonly TileVerticalRect[],
): number | null {
  if (rects.length === 0) return null
  const midpoints = rects.map((r) => r.top + r.height / 2)
  const idx = computeDropIndexFromMidpoints(clientY, midpoints)
  if (idx <= 0) return rects[0].top
  if (idx >= rects.length) return rects[rects.length - 1].bottom
  return (rects[idx - 1].bottom + rects[idx].top) / 2
}

/**
 * FEAT-2026-07-20-KANBAN-PRIORITY-LANE-ORDER — resolve the priority BAND a card
 * dropped at `dropIndex` lands in, so a cross-band drag can ADOPT that priority
 * and actually stay where it was dropped. Without this the priority-first lane
 * comparator (utils.ts compareCards) would immediately snap a lower-priority
 * card back down below the higher-priority band it was dropped into.
 *
 * Returns the priority of the neighbour immediately ABOVE the insertion point
 * (the band the card joins from the top), falling back to the neighbour below,
 * or `null` when the drop has no neighbours (empty lane / no tiles) so callers
 * can treat "nothing to adopt" without special-casing.
 *
 * Pure + DOM-free, and mirrors computeReorderPosition's neighbour model exactly
 * (same moving-card filter, same clamp) so the adopted band and the computed
 * position always agree about who the neighbours are.
 */
export function computeDropAdoptPriority(
  orderedCards: ReadonlyArray<{ id: string; priority?: number | null }>,
  dropIndex: number,
  movingCardId: string,
): number | null {
  const list = orderedCards.filter((c) => c.id !== movingCardId)
  const n = list.length
  if (n === 0) return null
  const clamped = Math.max(0, Math.min(dropIndex, n))
  const above = clamped > 0 ? list[clamped - 1] : null
  const below = clamped < n ? list[clamped] : null
  const ref = above ?? below
  const p = ref?.priority
  return typeof p === 'number' && Number.isFinite(p) ? p : null
}

export function computeReorderPosition(
  orderedCards: ReadonlyArray<{ id: string; position?: number | null }>,
  dropIndex: number,
  movingCardId: string,
): number {
  // The moving card must not count as its own neighbour — removing it also
  // makes same-lane and cross-lane drops share one code path (cross-lane: the
  // card isn't in the list yet, so the filter is a no-op).
  const list = orderedCards.filter((c) => c.id !== movingCardId)
  const n = list.length
  const clamped = Math.max(0, Math.min(dropIndex, n))

  const before = clamped > 0 ? finitePosition(list[clamped - 1]) : null
  const after = clamped < n ? finitePosition(list[clamped]) : null

  if (before !== null && after !== null) {
    if (after > before) return (before + after) / 2
    // BUG-2026-07-06-KANBAN-TIED-POSITIONS-TELEPORT: when BOTH neighbours
    // share the same stored position (the norm in never-manually-ordered
    // lanes, where every imported/auto-created card sits at 0.0), NO float
    // can land between them; any value above the tie sorts AFTER every
    // tied card in the lane (compareCards breaks ties by priority/due/
    // created, not adjacency). The old "half-step above" answer teleported
    // the card to the bottom of an 11k-card lane, past the 2000-row fetch
    // cap, so it looked deleted. Return the tied value itself: the card
    // stays inside the tie group (visible, non-destructive) instead of
    // out-ranking the whole lane.
    if (after === before) return before
    // Genuinely out-of-order stored positions (before > after): sit a
    // half-step above the upper neighbour so the card lands after it.
    return before + POSITION_STEP / 2
  }
  if (before !== null) return before + POSITION_STEP
  if (after !== null) return after - POSITION_STEP
  return POSITION_STEP
}
